import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import { 
  Cloud, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Send,
  Eye,
  Trash2,
  AlertTriangle,
  Clock,
  Settings,
  Play,
  RotateCcw,
  Info,
  ShieldOff,
  Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function SalesforcePanel() {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState(null);
  const [outbox, setOutbox] = useState({ entries: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [formData, setFormData] = useState({
    salesforce_instance_url: "",
    client_id: "",
    client_secret: "",
    custom_object_name: "Kanban_Event__c"
  });

  // Check if Salesforce is in stubbed/inactive mode (STUB or DRY_RUN)
  const isStubbed = settings?.is_stubbed || settings?.salesforce_mode === "STUB" || settings?.salesforce_mode === "DRY_RUN";
  const currentMode = settings?.salesforce_mode || "STUB";

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/salesforce/settings`, { headers: getAuthHeader() });
      setSettings(res.data);
      setFormData({
        salesforce_instance_url: res.data.salesforce_instance_url || "",
        client_id: res.data.client_id || "",
        client_secret: "",
        custom_object_name: res.data.custom_object_name || "Kanban_Event__c"
      });
    } catch (error) {
      console.error("Failed to fetch Salesforce settings");
    }
  };

  const fetchOutbox = async () => {
    try {
      const res = await axios.get(`${API}/admin/salesforce/outbox?limit=20`, { headers: getAuthHeader() });
      setOutbox(res.data);
    } catch (error) {
      console.error("Failed to fetch outbox");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchSettings();
      await fetchOutbox();
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSaveSettings = async () => {
    try {
      const params = new URLSearchParams();
      if (formData.salesforce_instance_url) params.append("salesforce_instance_url", formData.salesforce_instance_url);
      if (formData.client_id) params.append("client_id", formData.client_id);
      if (formData.client_secret) params.append("client_secret", formData.client_secret);
      if (formData.custom_object_name) params.append("custom_object_name", formData.custom_object_name);
      
      await axios.put(`${API}/admin/salesforce/settings?${params.toString()}`, {}, { headers: getAuthHeader() });
      toast.success("Settings saved");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleToggleEnabled = async () => {
    try {
      const newEnabled = !settings?.salesforce_enabled;
      await axios.put(`${API}/admin/salesforce/settings?salesforce_enabled=${newEnabled}`, {}, { headers: getAuthHeader() });
      toast.success(newEnabled ? "Salesforce enabled" : "Salesforce disabled");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to toggle");
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/salesforce/test-connection`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success("Connection successful!");
      } else {
        toast.error(res.data.error || "Connection failed");
      }
    } catch (error) {
      // Handle STUB gate 409 response
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        toast.error(detail?.message || "Salesforce is inactive for this release");
      } else {
        toast.error(normalizeError(error.response?.data) || "Test failed");
      }
    } finally {
      setTesting(false);
    }
  };

  const handleProcessOutbox = async (dryRun = false) => {
    if (dryRun) {
      setDryRunning(true);
      setDryRunResult(null);
    } else {
      setProcessing(true);
    }
    
    try {
      const res = await axios.post(`${API}/admin/salesforce/process-outbox?dry_run=${dryRun}&limit=50`, {}, { headers: getAuthHeader() });
      
      if (dryRun) {
        setDryRunResult(res.data);
        toast.info(`Dry run: ${res.data.total} events would be sent`);
      } else {
        toast.success(`Processed: ${res.data.succeeded} sent, ${res.data.failed} failed`);
        fetchOutbox();
      }
    } catch (error) {
      // Handle STUB gate 409 response
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        toast.error(detail?.message || "Salesforce is inactive for this release");
      } else {
        toast.error(normalizeError(error.response?.data) || "Processing failed");
      }
    } finally {
      setProcessing(false);
      setDryRunning(false);
    }
  };

  const handleRetryFailed = async () => {
    try {
      const res = await axios.post(`${API}/admin/salesforce/retry-failed`, {}, { headers: getAuthHeader() });
      toast.success(`Reset ${res.data.reset_count} failed entries`);
      fetchOutbox();
    } catch (error) {
      // Handle STUB gate 409 response
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        toast.error(detail?.message || "Salesforce is inactive for this release");
      } else {
        toast.error("Failed to retry");
      }
    }
  };

  const handleClearSent = async () => {
    try {
      const res = await axios.delete(`${API}/admin/salesforce/outbox?status=SENT`, { headers: getAuthHeader() });
      toast.success(`Cleared ${res.data.deleted_count} sent entries`);
      fetchOutbox();
    } catch (error) {
      toast.error("Failed to clear");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING": return "bg-yellow-900/30 text-yellow-400 border-yellow-900/50";
      case "SENT": return "bg-green-900/30 text-green-400 border-green-900/50";
      case "FAILED": return "bg-red-900/30 text-red-400 border-red-900/50";
      case "SKIPPED": return "bg-gray-900/30 text-gray-400 border-gray-900/50";
      case "STUBBED": return "bg-purple-900/30 text-purple-400 border-purple-900/50";
      default: return "";
    }
  };
  
  const getModeColor = (mode) => {
    switch (mode) {
      case "LIVE": return "bg-green-900/50 text-green-400 border-green-700";
      case "DRY_RUN": return "bg-blue-900/50 text-blue-400 border-blue-700";
      case "STUB": return "bg-orange-900/50 text-orange-400 border-orange-700";
      default: return "bg-gray-900/50 text-gray-400";
    }
  };
  
  const getModeLabel = (mode) => {
    switch (mode) {
      case "LIVE": return "LIVE";
      case "DRY_RUN": return "DRY RUN";
      case "STUB": return "INACTIVE (Stubbed)";
      default: return mode;
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading Salesforce settings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* STUB Mode Banner - Show prominently when stubbed */}
      {isStubbed && (
        <div className="p-4 bg-orange-900/20 border border-orange-700/50 rounded-sm flex items-start gap-3" data-testid="stub-banner">
          <ShieldOff className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-orange-400">Salesforce Integration Inactive</p>
            <p className="text-sm text-orange-300/80 mt-1">
              {settings?.stub_message || "Stubbed for this release. No outbound network calls are made to Salesforce."}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Mode: <code className="text-orange-400">{currentMode}</code> — To enable real API calls, change mode to LIVE in database settings.
            </p>
          </div>
        </div>
      )}
      
      {/* Status Card */}
      <Card className="industrial-card border-primary/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Cloud className="w-5 h-5 text-primary" />
            SALESFORCE INTEGRATION STATUS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Mode</p>
              <Badge className={getModeColor(currentMode)} data-testid="salesforce-mode-badge">
                {getModeLabel(currentMode)}
              </Badge>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Enabled</p>
              <Badge className={settings?.salesforce_enabled ? "bg-green-900/50 text-green-400" : "bg-gray-900/50 text-gray-400"}>
                {settings?.salesforce_enabled ? "YES" : "NO"}
              </Badge>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Credentials</p>
              <Badge className={settings?.client_secret_configured ? "bg-green-900/50 text-green-400" : "bg-yellow-900/50 text-yellow-400"}>
                {settings?.client_secret_configured ? "CONFIGURED" : "NOT SET"}
              </Badge>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Pending</p>
              <p className="font-mono text-2xl text-yellow-400">{outbox.stats?.pending || 0}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Failed</p>
              <p className="font-mono text-2xl text-red-400">{outbox.stats?.failed || 0}</p>
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between p-4 bg-muted/30 rounded-sm">
            <div>
              <p className="text-sm font-medium">Salesforce Integration</p>
              <p className="text-xs text-muted-foreground">
                {isStubbed 
                  ? "Inactive for this release — Events are queued locally only" 
                  : settings?.salesforce_enabled 
                    ? "Events are being pushed to Salesforce" 
                    : "Events are queued locally only"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isStubbed && <Lock className="w-4 h-4 text-orange-400" />}
              <Switch
                checked={settings?.salesforce_enabled || false}
                onCheckedChange={handleToggleEnabled}
                disabled={isStubbed}
                data-testid="salesforce-toggle"
              />
              <span className="text-sm">{settings?.salesforce_enabled ? "On" : "Off"}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card className="industrial-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            CONFIGURATION
          </CardTitle>
          <CardDescription>Configure Salesforce Connected App credentials</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase">Instance URL</Label>
              <Input
                value={formData.salesforce_instance_url}
                onChange={(e) => setFormData({ ...formData, salesforce_instance_url: e.target.value })}
                placeholder="https://yourorg.salesforce.com"
                className="industrial-input"
                data-testid="sf-instance-url"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase">Custom Object</Label>
              <Input
                value={formData.custom_object_name}
                onChange={(e) => setFormData({ ...formData, custom_object_name: e.target.value })}
                placeholder="Kanban_Event__c"
                className="industrial-input"
                data-testid="sf-custom-object"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase">Client ID</Label>
              <Input
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                placeholder="Connected App Client ID"
                className="industrial-input"
                data-testid="sf-client-id"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase">Client Secret</Label>
              <Input
                type="password"
                value={formData.client_secret}
                onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                placeholder={settings?.client_secret_configured ? "••••••••" : "Enter secret"}
                className="industrial-input"
                data-testid="sf-client-secret"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button onClick={handleSaveSettings} className="uppercase" data-testid="save-sf-settings-btn">
              Save Settings
            </Button>
            <Button 
              onClick={handleTestConnection} 
              variant="secondary" 
              disabled={testing || isStubbed}
              className="uppercase"
              data-testid="test-sf-connection-btn"
            >
              {isStubbed ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <RefreshCw className={`w-4 h-4 mr-2 ${testing ? "animate-spin" : ""}`} />
              )}
              {testing ? "Testing..." : isStubbed ? "Blocked (Stubbed)" : "Test Connection"}
            </Button>
          </div>
          
          {isStubbed && (
            <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-sm flex items-start gap-2">
              <Lock className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <span className="text-xs text-orange-300/80">
                Test Connection is blocked while Salesforce is inactive. Credentials can still be saved for future use.
              </span>
            </div>
          )}
          
          <div className="p-3 bg-muted/30 rounded-sm flex items-start gap-2">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <span className="text-xs text-muted-foreground">
              Create a Connected App in Salesforce with Client Credentials Flow enabled. 
              See <code className="text-primary">/docs/salesforce-integration.md</code> for setup guide.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Outbox Controls */}
      <Card className="industrial-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Send className="w-5 h-5" />
            OUTBOX CONTROLS
          </CardTitle>
          <CardDescription>
            {isStubbed 
              ? "Outbound operations blocked — Salesforce inactive for this release" 
              : "Manage pending events and push to Salesforce"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isStubbed && (
            <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-sm flex items-start gap-2">
              <Lock className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <span className="text-xs text-orange-300/80">
                Send and Retry operations are blocked. Dry Run (preview) is still available to see what would be sent.
              </span>
            </div>
          )}
          
          <div className="flex gap-3 flex-wrap">
            <Button 
              onClick={() => handleProcessOutbox(true)} 
              variant="secondary"
              disabled={dryRunning || outbox.stats?.pending === 0}
              className="uppercase"
              data-testid="dry-run-btn"
            >
              <Eye className={`w-4 h-4 mr-2 ${dryRunning ? "animate-pulse" : ""}`} />
              {dryRunning ? "Running..." : "Dry Run (Preview)"}
            </Button>
            
            <Button 
              onClick={() => handleProcessOutbox(false)} 
              disabled={processing || isStubbed || !settings?.salesforce_enabled || outbox.stats?.pending === 0}
              className="uppercase"
              data-testid="send-pending-btn"
            >
              {isStubbed ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <Play className={`w-4 h-4 mr-2 ${processing ? "animate-spin" : ""}`} />
              )}
              {processing ? "Sending..." : isStubbed ? "Blocked" : `Send Pending (${outbox.stats?.pending || 0})`}
            </Button>
            
            <Button 
              onClick={handleRetryFailed} 
              variant="outline"
              disabled={isStubbed || outbox.stats?.failed === 0}
              className="uppercase"
              data-testid="retry-failed-btn"
            >
              {isStubbed ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {isStubbed ? "Blocked" : `Retry Failed (${outbox.stats?.failed || 0})`}
            </Button>
            
            <Button 
              onClick={handleClearSent} 
              variant="outline"
              disabled={outbox.stats?.sent === 0}
              className="uppercase"
              data-testid="clear-sent-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Sent ({outbox.stats?.sent || 0})
            </Button>
          </div>
          
          {/* Dry Run Results */}
          {dryRunResult && (
            <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-blue-400">Dry Run Results</span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {dryRunResult.total} events would be sent to Salesforce
              </p>
              {dryRunResult.details?.slice(0, 3).map((d, i) => (
                <div key={i} className="text-xs font-mono bg-muted/30 p-2 rounded mb-1">
                  {d.event_id?.slice(0, 8)}... → {d.payload?.Event_Type__c}
                </div>
              ))}
              {dryRunResult.total > 3 && (
                <p className="text-xs text-muted-foreground">...and {dryRunResult.total - 3} more</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outbox Entries */}
      <Card className="industrial-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              OUTBOX ENTRIES
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchOutbox}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {outbox.entries?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No outbox entries</p>
              </div>
            ) : (
              <div className="space-y-2">
                {outbox.entries?.map((entry) => (
                  <div 
                    key={entry.id}
                    className="p-3 bg-muted/30 rounded-sm flex items-center justify-between"
                    data-testid={`outbox-entry-${entry.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={getStatusColor(entry.status)}>
                          {entry.status}
                        </Badge>
                        <span className="font-mono text-sm">{entry.event_type}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ID: {entry.event_id?.slice(0, 12)}... | 
                        Attempts: {entry.attempts} | 
                        Created: {new Date(entry.created_at).toLocaleString()}
                      </p>
                      {entry.last_error && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {entry.last_error.slice(0, 80)}...
                        </p>
                      )}
                    </div>
                    {entry.status === "SENT" && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    {entry.status === "FAILED" && (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
